export interface SeenNotification {
  id: number;
  eventType: string;
  readAt?: string | null;
}

/**
 * Which notifications in `current` are unread AND were not in the previously
 * seen set — i.e. what arrived since the last poll and deserves a toast.
 *
 * `seen === null` means this is the first load of the session: it establishes the
 * baseline and toasts nothing, so opening the app never replays history.
 */
export function newUnreadSince<T extends SeenNotification>(
  seen: ReadonlySet<number> | null,
  current: readonly T[]
): T[] {
  if (seen === null) return [];
  return current.filter(n => !n.readAt && !seen.has(n.id));
}

/**
 * Which arrivals should interrupt with a toast.
 *
 * Two independent questions, deliberately kept apart: everything new lands in the
 * bell (`newUnreadSince`), and only the event types the user asked to be
 * interrupted about are toasted. An empty `toastEnabled` — which is also the state
 * while preferences are still loading — means no toast, never a burst of them.
 */
export function arrivalsToToast<T extends SeenNotification>(
  seen: ReadonlySet<number> | null,
  current: readonly T[],
  toastEnabled: ReadonlySet<string>
): T[] {
  return newUnreadSince(seen, current).filter(n =>
    toastEnabled.has(n.eventType)
  );
}
